import React, { useEffect, useState, useRef } from 'react';
import { BACKEND_URL } from '../config';
import { useParams, useLocation, Link } from 'react-router-dom';
import { api } from '../api/client';
import { Spin, Alert} from 'antd';
import { useNavigate } from "react-router-dom";
import { useAuth } from '../auth/AuthContext';
import * as favoritesApi from '../api/favorites';
import { isPremium, isAdmin } from '../utils/userAccess';
import StarIcon from '../assets/icons/star.svg';
import StarFillIcon from '../assets/icons/star-fill.svg';

import './RecipeDetail.css';
import DogProfileModal, { DogProfile } from '../components/DogProfileModal';
import FlashMessage from '../components/FlashMessage';
import { capitalizeFirst } from '../utils/stringHelpers';
import pawImg from '../assets/paw.png';
import buttonStyles from '../components/Buttons.module.css';
import { isMobileView } from '../utils/deviceUtils';
import { ReactComponent as ScalesIcon } from '../assets/icons/scales.svg';
import { ReactComponent as HouseIcon } from '../assets/icons/house.svg';
import { ReactComponent as InfoIcon } from '../assets/icons/info.svg';
import DogIcon from '../assets/icons/dog-svgrepo-com (1).svg';

interface Recipe {
  id: number;
  slug: string;
  title: string;
  photo_url: string;
  ingredients: Ingredient[];
  instructions: string;
  subtitle?: string;
}

interface PortionData {
  recipe_id: number;
  age_months: number;
  weight_kg: number;
  meals_per_day: number;
  body_condition: string;
  daily_grams: number;
  grams_per_meal: number;
  cups_per_meal: number;
  cups_per_day: number;
  recipe_days: number;
  recipe_meals: number;
}

interface Ingredient {
  name: string;
  quantity: string;
  category: string; // 'supplement_optional', 'supplement', or other
  optional: boolean;
  supplement_path: boolean;
  whole_food_path: boolean;
}

const normalizeAgeYears = (s: string) => {
  if (typeof s === 'number') return s;
  if (s === '>1') return 1;
  if (s === '<15') return 15;
  return Number(s); // "8", "9", etc.
};

const normalizeWeightKg = (value: string | number) => {
  if (value == null) return NaN;

  // If already a number, assume kg
  if (typeof value === 'number') return value;

  const s = String(value).trim();

  // Parse ranges or singles
  const toNumber = (x: string) => Number(x);

  if (s.includes('-')) {
    const [aStr, bStr] = s.split('-');
    const a = toNumber(aStr);
    const b = toNumber(bStr);
    const mid = (a + (isNaN(b) ? a : b)) / 2;
    // If the midpoint is clearly grams, convert; else keep as kg
    return mid >= 1000 ? mid / 1000 : mid;
  }

  const n = Number(s);
  return n >= 1000 ? n / 1000 : n; // e.g., "20000" (g) -> 20 kg
};


const RecipeDetail: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const showPortionsInUrl = searchParams.has('showPortions');
  const [showUpdated, setShowUpdated] = useState(false);
  const [ariaMessage, setAriaMessage] = useState('');
  const isMobile = isMobileView();
  const navigate = useNavigate();
  const { slug } = useParams(); // Get the recipe slug from the URL
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [isLocked, setIsLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // const [showPortions, setShowPortions] = useState<boolean>(false);
  const [portionExpanded, setPortionExpanded] = useState<boolean>(false);
  const [portionData, setPortionData] = useState<PortionData | null>(null);
  const [portionLoading, setPortionLoading] = useState<boolean>(false);
  const [portionMultiplier, setPortionMultiplier] = useState<number>(1); 
  const [batchSize, setBatchSize] = useState<string>('Normal');
  const [isBatchResize, setIsBatchResize] = useState<boolean>(false);
  const [isFavorited, setIsFavorited] = useState<boolean>(false);
  const [favoriteId, setFavoriteId] = useState<number | null>(null);
  const [favoriteLoading, setFavoriteLoading] = useState<boolean>(false);
  const [justSaved, setJustSaved] = useState<boolean>(false);
  const [flashMessage, setFlashMessage] = useState<{ message: string | React.ReactNode; type: 'success' | 'error' } | null>(null);
  const [hasInteracted, setHasInteracted] = useState<boolean>(false);
  const [showNavigateWarning, setShowNavigateWarning] = useState<boolean>(false);
  const [showLeaveModal, setShowLeaveModal] = useState<boolean>(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const { user, refreshUser } = useAuth();

  // Calculate free portion calculations remaining for display
  const freePortionCalculationsRemaining = user && !isPremium(user) && !isAdmin(user) 
    ? (user.calculations_remaining ?? 0)
    : 0;

  // Radio selection state for balancing method
  const [selectedPath, setSelectedPath] = React.useState<'supplement' | 'wholefood' | null>('supplement');
  const [openProfileModal, setOpenProfileModal] = useState(false);
  const [showCalcTooltip, setShowCalcTooltip] = useState(false);
  const defaultProfile: DogProfile = { age: '', weight: '', meals_per_day: 1, allergies: '', body_condition: '' };

  const [dogProfile, setDogProfile] = useState<DogProfile>(() => {
    // Check if coming from builder FIRST before deciding to load from storage
    const params = new URLSearchParams(window.location.search);
    const fromBuilder = params.get('fromBuilder');
    
    if (!fromBuilder) {
      // NOT from builder, start fresh and clear storage
      localStorage.removeItem('dogProfile');
      return defaultProfile;
    }
    
    // Coming from builder, load from localStorage
    const stored = localStorage.getItem('dogProfile');
    if (!stored) return defaultProfile;

    try {
      const p = JSON.parse(stored);
      // Create a new object to avoid mutating the parsed one
      const normalized: DogProfile = {
        ...p,
        weight: String(normalizeWeightKg(p.weight)),
        age: String(normalizeAgeYears(p.age)),
        // body_condition: p.body_condition || '',
      };
      localStorage.setItem('dogProfile', JSON.stringify(normalized)); // persist migration
      return normalized;
    } catch {
      return defaultProfile;
    }
  });

  const handleToggleFavorite = async () => {
    if (!recipe || favoriteLoading) return;
    
    setFavoriteLoading(true);
    try {
      if (isFavorited && favoriteId) {
        // Remove from favorites
        await favoritesApi.removeFavorite(favoriteId);
        setIsFavorited(false);
        setFavoriteId(null);
        setJustSaved(false);
        setAriaMessage('Recipe removed from favorites');
      } else {
        // Add to favorites
        const newFavorite = await favoritesApi.addFavorite(recipe.id);
        setIsFavorited(true);
        setFavoriteId(newFavorite.id);
        setJustSaved(true);
        // Reset animation after it completes
        setTimeout(() => setJustSaved(false), 600);
        setAriaMessage('Recipe added to favorites');
      }
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
      setAriaMessage('Failed to update favorites');
    } finally {
      setFavoriteLoading(false);
    }
  };

  // On mount, if coming from builder (flag in URL), set batchSize/portionMultiplier from param/localStorage, then clear flag
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromBuilder = params.get('fromBuilder');
    if (fromBuilder) {
      // Try to get from URL params first
      const urlBatch = params.get('batchSize');
      const urlMult = params.get('portionMultiplier');
      if (urlBatch) setBatchSize(urlBatch);
      if (urlMult) setPortionMultiplier(Number(urlMult));
      // Optionally clear the flag so future profile changes work as normal
      params.delete('fromBuilder');
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    }
  }, []);


  // Snapshot for restoring dog profile if modal is cancelled
  const dogProfileSnapshot = useRef<DogProfile | null>(null);
  const [showAllInstructions, setShowAllInstructions] = useState(true);

  let dailyGrams = 0;
  let gramsPerMeal = 0;
  let recipeDays = 0;
  let recipeMeals = 0;

  if (portionData) {
    dailyGrams   = portionData.daily_grams;
    gramsPerMeal = portionData.grams_per_meal;
    recipeDays   = portionData.recipe_days;
    recipeMeals  = portionData.recipe_meals;
  }
  
  // Use backend values directly for display (no frontend rounding)
  const displayMealCups = portionData && portionData.cups_per_meal !== undefined ? portionData.cups_per_meal : '';
  const displayDailyCups = portionData && portionData.cups_per_day !== undefined ? portionData.cups_per_day : '';


  // handles "1.5", "1 1/2", "½", then preserves the rest of the string
  const parseNumber = (s: string) => {
    const frac = s.match(/(\d+)\s+(\d+)\/(\d+)/);      // "1 1/2"
    if (frac) return +frac[1] + (+frac[2] / +frac[3]);
    const uni = s.match(/½|¼|¾/);                      // quick map for common unicode
    if (uni) return { "½":0.5, "¼":0.25, "¾":0.75 }[uni[0]];
    return parseFloat(s);
  };

  // Accordion open state per ingredient index
  const [ingredientAccordionOpen, setIngredientAccordionOpen] = React.useState<{ [key: number]: boolean }>({});

  type ProduceApproxType = "count" | "volume";

  const produceMap: Record<string, { avgWeight: number; label: string; type: ProduceApproxType }> = {
    "eggshell powder": {
      avgWeight: 100, // 1 cup eggshell powder ≈ 100g (typical)
      label: "cups eggshell powder (about 12 eggs)",
      type: "volume",
    },
    "sweet potato": { avgWeight: 150, label: "medium sweet potatoes", type: "count" },
    carrot: { avgWeight: 50, label: "medium carrots", type: "count" },
    egg: { avgWeight: 55, label: "eggs", type: "count" },
    "brown rice": { avgWeight: 195, label: "cups cooked brown rice", type: "volume" },
    kale: { avgWeight: 20, label: "cups chopped kale", type: "volume" },

    // --- Eggs (by type) ---
    "chicken egg": { avgWeight: 50, label: "eggs", type: "count" },
    "duck egg": { avgWeight: 70, label: "duck eggs", type: "count" },
    "quail egg": { avgWeight: 10, label: "quail eggs", type: "count" },

    // --- Veggies ---
    "green bean": {
      avgWeight: 125,
      label: "cups cooked green beans",
      type: "volume",
    },
    broccoli: {
      avgWeight: 90,
      label: "cups chopped broccoli",
      type: "volume",
    },
    // cauliflower: {
    //   avgWeight: 100,
    //   label: "cups chopped cauliflower",
    //   type: "volume",
    // },
    spinach: {
      avgWeight: 30,
      label: "cups fresh spinach",
      type: "volume",
    },
    pumpkin: {
      avgWeight: 120,
      label: "cups diced pumpkin",
      type: "volume",
    },
    "butternut squash": {
      avgWeight: 140,
      label: "cups diced butternut squash",
      type: "volume",
    },
    zucchini: {
      avgWeight: 120,
      label: "cups sliced zucchini",
      type: "volume",
    },
    // cucumber: {
    //   avgWeight: 100,
    //   label: "cups sliced cucumber",
    //   type: "volume",
    // },
    // potato: {
    //   avgWeight: 170,
    //   label: "medium potatoes",
    //   type: "count",
    // },
    pea: {
      avgWeight: 145,
      label: "cups peas",
      type: "volume",
    },
    // "brussels sprout": {
    //   avgWeight: 20,
    //   label: "brussels sprouts",
    //   type: "count",
    // },
    // celery: {
    //   avgWeight: 100,
    //   label: "cups chopped celery",
    //   type: "volume",
    // },
    // beet: {
    //   avgWeight: 85,
    //   label: "medium beets",
    //   type: "count",
    // },
    // parsnip: {
    //   avgWeight: 75,
    //   label: "medium parsnips",
    //   type: "count",
    // },
    // turnip: {
    //   avgWeight: 120,
    //   label: "medium turnips",
    //   type: "count",
    // },
    // lettuce: {
    //   avgWeight: 35,
    //   label: "cups shredded lettuce",
    //   type: "volume",
    // },
    // cabbage: {
    //   avgWeight: 90,
    //   label: "cups shredded cabbage",
    //   type: "volume",
    // },
    // "bok choy": {
    //   avgWeight: 70,
    //   label: "cups chopped bok choy",
    //   type: "volume",
    // },
    "bell pepper": {
      avgWeight: 120,
      label: "medium bell peppers",
      type: "count",
    },
    // asparagus: {
    //   avgWeight: 15,
    //   label: "asparagus spears",
    //   type: "count",
    // },
    // okra: {
    //   avgWeight: 100,
    //   label: "cups sliced okra",
    //   type: "volume",
    // },
    // "nori seaweed": {
    //   avgWeight: 3,
    //   label: "sheets of nori",
    //   type: "count",
    // },
    // edamame: {
    //   avgWeight: 155,
    //   label: "cups cooked edamame",
    //   type: "volume",
    // },
    // chard: {
    //   avgWeight: 35,
    //   label: "cups chopped chard",
    //   type: "volume",
    // },
    // "collard greens": {
    //   avgWeight: 65,
    //   label: "cups chopped collard greens",
    //   type: "volume",
    // },

    // --- Fruits ---
    // apple: {
    //   avgWeight: 180,
    //   label: "medium apples",
    //   type: "count",
    // },
    // banana: {
    //   avgWeight: 120,
    //   label: "medium bananas",
    //   type: "count",
    // },
    blueberry: {
      avgWeight: 148,
      label: "cups blueberries",
      type: "volume",
    },
    // strawberry: {
    //   avgWeight: 166,
    //   label: "cups sliced strawberries",
    //   type: "volume",
    // },
    // raspberry: {
    //   avgWeight: 123,
    //   label: "cups raspberries",
    //   type: "volume",
    // },
    // blackberry: {
    //   avgWeight: 144,
    //   label: "cups blackberries",
    //   type: "volume",
    // },
    // watermelon: {
    //   avgWeight: 150,
    //   label: "cups diced watermelon",
    //   type: "volume",
    // },
    // cantaloupe: {
    //   avgWeight: 160,
    //   label: "cups diced cantaloupe",
    //   type: "volume",
    // },
    // honeydew: {
    //   avgWeight: 170,
    //   label: "cups diced honeydew",
    //   type: "volume",
    // },
    // pineapple: {
    //   avgWeight: 165,
    //   label: "cups pineapple chunks",
    //   type: "volume",
    // },
    // mango: {
    //   avgWeight: 165,
    //   label: "cups sliced mango",
    //   type: "volume",
    // },
    // pear: {
    //   avgWeight: 178,
    //   label: "medium pears",
    //   type: "count",
    // },
    // peach: {
    //   avgWeight: 150,
    //   label: "medium peaches",
    //   type: "count",
    // },
    // plum: {
    //   avgWeight: 66,
    //   label: "medium plums",
    //   type: "count",
    // },
    // kiwi: {
    //   avgWeight: 75,
    //   label: "kiwis",
    //   type: "count",
    // },
    // cranberry: {
    //   avgWeight: 100,
    //   label: "cups cranberries",
    //   type: "volume",
    // },
    // coconut: {
    //   avgWeight: 80,
    //   label: "cups shredded coconut",
    //   type: "volume",
    // },
    // papaya: {
    //   avgWeight: 140,
    //   label: "cups diced papaya",
    //   type: "volume",
    // },
    // date: {
    //   avgWeight: 24,
    //   label: "Medjool dates",
    //   type: "count",
    // },
    // fig: {
    //   avgWeight: 50,
    //   label: "medium figs",
    //   type: "count",
    // },
    // "goji berry": {
    //   avgWeight: 112,
    //   label: "cups dried goji berries",
    //   type: "volume",
    // },

    // --- Plant proteins / grains / dairy / broths ---
    // lentil: {
    //   avgWeight: 198,
    //   label: "cups cooked lentils",
    //   type: "volume",
    // },
    // chickpea: {
    //   avgWeight: 164,
    //   label: "cups cooked chickpeas",
    //   type: "volume",
    // },
    // quinoa: {
    //   avgWeight: 185,
    //   label: "cups cooked quinoa",
    //   type: "volume",
    // },
    // oats: {
    //   avgWeight: 90,
    //   label: "cups dry oats",
    //   type: "volume",
    // },
    // "greek yogurt": {
    //   avgWeight: 245,
    //   label: "cups Greek yogurt",
    //   type: "volume",
    // },
    // "cottage cheese": {
    //   avgWeight: 225,
    //   label: "cups cottage cheese",
    //   type: "volume",
    // },
    "bone broth": {
      avgWeight: 240,
      label: "cups bone broth",
      type: "volume",
    },
    // "beef broth": {
    //   avgWeight: 240,
    //   label: "cups beef broth",
    //   type: "volume",
    // },
    // "chicken broth": {
    //   avgWeight: 240,
    //   label: "cups chicken broth",
    //   type: "volume",
    // },
  };

  const unicodeFractionMap: Record<string, string> = {
    "¼": " 1/4",
    "½": " 1/2",
    "¾": " 3/4",
  };

  const normalizeIngredientKey = (name: string) =>
    name
      .toLowerCase()
      .trim()
      .replace(/_/g, " ")    // chicken_eggs -> chicken eggs
      .replace(/\bpotatos\b/g, "potato") // fix typo variant
      .replace(/s\b/g, "");  // eggs -> egg, carrots -> carrot


  // Helper: grams to US household units (approximate)
  function gramsToHouseholdUnits(grams: number, ingredientName: string): string {
    if (isNaN(grams) || grams <= 0) return '';

    const lowerName = ingredientName.toLowerCase();
    // Remove parentheticals and extra spaces for robust matching
    const normName = normalizeIngredientKey(ingredientName.replace(/\([^)]*\)/g, '').replace(/\s+/g, ' ').trim());

    let matchedEntry: { avgWeight: number; label: string; type: ProduceApproxType } | null = null;
    for (const key in produceMap) {
      if (normName.includes(key)) {
        matchedEntry = produceMap[key];
        break;
      }
    }

    // Special case for kale: show both cups and stalks
    if (normName.includes('kale')) {
      // 1 stalk ≈ 40g (typical)
      const avgStalkWeight = 40;
      const cups = grams / 20; // existing avgWeight for cups chopped kale
      const stalks = grams / avgStalkWeight;
      const roundedCups = cups < 1.5 ? cups.toFixed(1) : Math.round(cups);
      const roundedStalks = stalks < 1.5 ? stalks.toFixed(1) : Math.round(stalks);
      const roundedStalksStr = String(roundedStalks);
      return `≈ ${roundedCups} cups chopped kale • ≈ ${roundedStalksStr} kale stalk${parseFloat(roundedStalksStr) === 1 ? '' : 's'}`;
    }

    if (matchedEntry) {
      const units = grams / matchedEntry.avgWeight;
      const rounded = units < 1.5 ? Number(units.toFixed(1)) : Math.round(units);
      // Special: for eggshell powder, show egg count in label
      if (normName.includes('eggshell powder')) {
        // 1 cup eggshell powder ≈ 12 eggs (from label)
        const eggsPerCup = 12;
        const eggs = Math.round(units * eggsPerCup * 10) / 10; // show 1 decimal if not whole
        return `≈ ${rounded} cups eggshell powder (about ${eggs} eggs)`;
      }
      return `≈ ${rounded} ${matchedEntry.label}`;
    }

    // Special case: eggshell powder should use produceMap, not generic powder logic
    if (normName.includes("eggshell powder")) {
    } else if (lowerName.includes("oil") || lowerName.includes("powder")) {
      const tsp = grams / 5;
      const tbsp = tsp / 3;
      return `≈ ${tsp.toFixed(1)} tsp (${(tbsp).toFixed(1)} Tbsp)`;
    }
    // General dry/wet household units
    if (grams < 5) {
      return `${(grams / 5).toFixed(2)} tsp`;
    } else if (grams < 15) {
      const tsp = grams / 5;
      return tsp === 1 ? '1 tsp' : `${tsp.toFixed(2)} tsp`;
    } else if (grams < 45) {
      const tbsp = grams / 15;
      return tbsp === 1 ? '1 Tbsp' : `${tbsp.toFixed(2)} Tbsp`;
    } else {
      const cups = grams / 225;
      if (cups < 1) {
        // Show as e.g. 3 Tbsp + 2 tsp
        const tbsp = Math.floor(grams / 15);
        const rem = grams % 15;
        let approx = `${tbsp} Tbsp`;
        if (rem > 0) approx += ` + ${(rem / 5).toFixed(2)} tsp`;
        return `≈ ${approx}`;
      } else {
        return `≈ ${cups < 2 ? cups.toFixed(1) : Math.round(cups)} cup${cups >= 2 ? 's' : ''}`;
      }
    }
  }

  function gramsToLbOz(grams: number): string {
    if (isNaN(grams) || grams <= 0) return '';

    const totalOzRaw = grams / 28.3495;
    const totalOz = +(totalOzRaw.toFixed(1)); // rounded to 1 decimal for display

    const lb = Math.floor(totalOzRaw / 16);
    const ozRaw = totalOzRaw - lb * 16;

    let oz: string;
    if (Math.abs(Math.round(ozRaw) - ozRaw) < 0.05) {
      oz = String(Math.round(ozRaw));  // round to whole oz
    } else {
      oz = ozRaw.toFixed(1);           // 1 decimal
    }

    let result = '';
    if (lb > 0) result += `${lb} lb`;
    if (parseFloat(oz) > 0 || lb === 0)
      result += (lb > 0 ? ' ' : '') + `${oz} oz`;

    // ⭐ Add total ounces so scaling is obvious
    result += ` (${totalOz} oz total)`;

    return result.trim();
  }

  const parseAndScaleQuantity = (
    quantity: string,
    multiplier: number,
    ingredientName: string
  ): {
    grams: string;
    lbOz: string;
    approx: string;
    isGram: boolean;
    nonGram: string;
  } => {
    if (!quantity.trim()) {
      return { grams: "", lbOz: "", approx: "", isGram: false, nonGram: "" };
    }

    // Stricter regex: number-ish prefix must start with digit
    const m = quantity.match(/^(\d[\d,\s./¼½¾]*)(.*)$/);
    if (!m) {
      return {
        grams: quantity,
        lbOz: "",
        approx: "",
        isGram: false,
        nonGram: quantity,
      };
    }

    let numStr = m[1].replace(/,/g, "").trim();
    const rest = m[2] ?? "";

    // Normalize unicode fractions before parsing
    numStr = numStr.replace(/[¼½¾]/g, (f) => unicodeFractionMap[f] || f);

    const numericValue = parseNumber(numStr) ?? 0;
    const scaled = numericValue * multiplier;

    // SPECIAL: Egg count handling (e.g., "8 eggs", "2 whole eggs")
    // Do NOT treat as grams unless the unit literally contains "g"
    if (/egg/i.test(ingredientName)) {
      // Still scale the number for eggs
      let formattedEggs: string;
      if (Number.isInteger(scaled)) {
        formattedEggs = scaled.toString();
      } else {
        const abs = Math.abs(scaled);
        const decimalPlaces = abs < 10 ? 2 : 1;
        formattedEggs = scaled.toFixed(decimalPlaces).replace(/\.00$/, "").replace(/\.0$/, "");
      }
      if (!isNaN(Number(formattedEggs)) && !formattedEggs.includes("e")) {
        const parts = formattedEggs.split(".");
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        formattedEggs = parts.join(".");
      }
      return {
        grams: '',
        lbOz: '',
        approx: '',
        isGram: false,
        nonGram: `${formattedEggs} ${rest}`.trim()
      };
    }

    // formatting policy: no decimals if .0, else up to 2 decimals for small numbers
    let formatted: string;
    if (Number.isInteger(scaled)) {
      formatted = scaled.toString();
    } else {
      const abs = Math.abs(scaled);
      const decimalPlaces = abs < 10 ? 2 : 1;
      formatted = scaled.toFixed(decimalPlaces);
      formatted = formatted
        .replace(/\.00$/, "")
        .replace(/\.0$/, "");
    }

    // Add commas for thousands if it's a "normal" number
    if (!isNaN(Number(formatted)) && !formatted.includes("e")) {
      const parts = formatted.split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      formatted = parts.join(".");
    }

    // If grams, show all formats; else just the value and unit
    if (/g(ram)?s?\b/i.test(rest)) {
      // Always use the scaled value for lbOz and approx
      const lbOz = gramsToLbOz(scaled);
      const approx = gramsToHouseholdUnits(scaled, ingredientName);
      return {
        grams: `${formatted} ${rest}`.trim(),
        lbOz,
        approx,
        isGram: true,
        nonGram: "",
      };
    } else {
      return {
        grams: "",
        lbOz: "",
        approx: "",
        isGram: false,
        nonGram: `${formatted} ${rest}`.trim(),
      };
    }
  };

  const handleProfileModalSubmit = (profile: DogProfile) => {
    // Normalize values before saving and recalculating
    const normalizedProfile: DogProfile = {
      ...profile,
      weight: String(normalizeWeightKg(profile.weight)),
      age: String(normalizeAgeYears(profile.age)),
      body_condition: profile.body_condition || '',
    };
    setDogProfile(normalizedProfile);
    localStorage.setItem('dogProfile', JSON.stringify(normalizedProfile));
    setOpenProfileModal(false);
    // Re-fetch portion data with new profile
    fetchPortionWithProfile(normalizedProfile);
    setShowUpdated(true);

    // setShowPortions(true);
    setPortionExpanded(true);

    setAriaMessage('Portion information updated.');
    setTimeout(() => {
      setShowUpdated(false);
      setAriaMessage('');
    }, 1500);
    // Clear snapshot after successful save
    dogProfileSnapshot.current = null;
  };

  const fetchPortionWithProfile = async (profile: DogProfile) => {
    setPortionLoading(true);
    setError(null);
    try {
      const age_years   = normalizeAgeYears(profile.age);
      const weight_kg   = normalizeWeightKg(profile.weight);
      const mealsPerDay = Number(profile.meals_per_day) || 1;
      const bodyCondition = profile.body_condition || '';

      const params = new URLSearchParams({
        slug: String(slug),
        age: String(age_years),
        weight: String(weight_kg),            // << send a plain number in kg
        meals_per_day: String(mealsPerDay),
        body_condition: bodyCondition,
        batch_size: batchSize,
      });

      if (profile.allergies) params.append('allergies', profile.allergies);
      if (isBatchResize) params.append('is_batch_resize', 'true');

      const res = await api.get(`/recipes/portion?${params.toString()}`);
      const data = res.data;
      setPortionData(data);
      setPortionLoading(false);
      
      // Reset batch resize flag after the request
      setIsBatchResize(false);
      
      // Refresh user data to update calculations_remaining count
      await refreshUser();
      
      // Mark that a free user has calculated portions (for navigation warning)
      if (user && !isPremium(user) && !isAdmin(user)) {
        setShowNavigateWarning(true);
      }
    } catch (e: any) {
      console.error('Error fetching portion data:', e);
      setPortionLoading(false);
      const errorCode = e?.response?.data?.error || e?.response?.data?.code;
      const errorMessage = e?.response?.data?.message;
      
      // Only show limit exceeded message if this was NOT a batch resize
      if (errorCode === 'PORTION_LIMIT_EXCEEDED' && !isBatchResize) {
        setFlashMessage({
          message: (
            <span>
              You have reached your portion calculation limit. {' '}
              <a href="/pricing" style={{ color: '#fff', textDecoration: 'underline', fontWeight: 'bold' }}>
                Upgrade to premium
              </a>
              {' '}for unlimited calculations
            </span>
          ),
          type: 'error'
        });
        // Don't collapse if coming from builder with showPortions=true
        const searchParams = new URLSearchParams(window.location.search);
        if (!searchParams.has('showPortions')) {
          setPortionExpanded(false);
        }
      } else if (errorCode !== 'PORTION_LIMIT_EXCEEDED') {
        setError(errorMessage || e.message || 'An unexpected error occurred.');
      }
    }
  };

  // Clear dog profile on unmount (when leaving the recipe details page)
  useEffect(() => {
    return () => {
      localStorage.removeItem('dogProfile');
    };
  }, []);

  // make sure desktop is always expanded 
  useEffect(() => {
    if (!isMobile) setPortionExpanded(true);
  }, [isMobile]);

  const portionToggleLabel = portionExpanded ? "Hide portions" : "Show portions";

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const show = searchParams.get('showPortions');
    const expanded = show === 'true';
    setPortionExpanded(expanded);

    const fetchRecipe = async () => {
      try {
        const response = await api.get(`/recipes/${slug}`);
        setRecipe(response.data);
        setIsLocked(false);
      } catch (err) {
        const status = (err as any)?.response?.status;
        if (status === 401 || status === 403) {
          // Show preview for both logged-out users (401) and users without access (403)
          setIsLocked(true);
          // Still try to get basic recipe info for preview
          const previewData = (err as any)?.response?.data?.recipe;
          if (previewData) {
            setRecipe(previewData);
          }
          setError(null);
          return;
        }
        setError("Recipe not found.");
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchRecipe();
    }
  }, [slug, navigate]);

  // Load favorites to check if current recipe is favorited
  useEffect(() => {
    const loadFavorites = async () => {
      if (!user || (!isPremium(user) && !isAdmin(user))) return;
      
      try {
        const favorites = await favoritesApi.getFavorites();
        
        if (recipe) {
          const existingFavorite = favorites.find(fav => fav.recipe_id === recipe.id);
          if (existingFavorite) {
            setIsFavorited(true);
            setFavoriteId(existingFavorite.id);
          }
        }
      } catch (err) {
        console.error('Failed to load favorites:', err);
      }
    };

    loadFavorites();
  }, [user, recipe]);

  // Fetch portions when batchSize, portionMultiplier, dogProfile, or portionExpanded changes (if profile is valid and expanded)
  useEffect(() => {
    if (
      !!recipe &&
      !isLocked &&
      portionExpanded &&
      dogProfile.age &&
      dogProfile.weight &&
      dogProfile.meals_per_day &&
      dogProfile.body_condition
    ) {
      fetchPortionWithProfile(dogProfile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchSize, portionMultiplier, dogProfile, portionExpanded, recipe, isLocked]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#fffbe6'
      }}>
        <Spin size="large" style={{ color: '#c14444' }} />
      </div>
    );
  }

  if (error) {
    return <Alert message={error} type="error" />;
  }

  if (isLocked) {
    return (
      <div className="recipe-bg-container">
        {/* <div className="recipe-paw-bg">
          <img src={pawImg} className="recipe-paw-float paw1" alt="" />
          <img src={pawImg} className="recipe-paw-float paw2" alt="" />
          <img src={pawImg} className="recipe-paw-float paw3" alt="" />
          <img src={pawImg} className="recipe-paw-float paw4" alt="" />
          <img src={pawImg} className="recipe-paw-float paw5" alt="" />
          <img src={pawImg} className="recipe-paw-float paw6" alt="" />
          <img src={pawImg} className="recipe-paw-float paw7" alt="" />
        </div> */}
        <div className="recipe-content-wrapper">
          <div style={{ maxWidth: 900, margin: '0 auto', padding: '2rem' }}>
            <button
              className={`${buttonStyles.antBtn} ${buttonStyles.antBtnSecondary}`}
              style={{ marginBottom: 18, fontSize: '1.08rem', padding: '0.7em 1.6em', borderRadius: '14px', fontWeight: 600 }}
              onClick={() => {
                const params = new URLSearchParams(window.location.search);
                const from = params.get('from');
                const scrollToRecipes = params.get('scrollToRecipes');
                const hasShowPortions = params.has('showPortions');
                
                if (hasShowPortions) {
                  navigate('/recipe-builder/results');
                } else if (from === 'home') {
                  navigate(scrollToRecipes === 'true' ? '/?scrollToRecipes=true' : '/');
                } else {
                  navigate('/recipes');
                }
              }}
            >
              ← Back to {(() => {
                const params = new URLSearchParams(window.location.search);
                if (params.has('showPortions')) return 'Results';
                if (params.get('from') === 'home') return 'Home';
                return 'Recipes';
              })()}
            </button>

            {recipe && (
              <>
                {/* Recipe Preview */}
                <div style={{ background: '#fff', borderRadius: 16, padding: '2rem', marginBottom: '1.5rem', position: 'relative' }}>
                  {/* Recipe Photo */}
                  <img 
                    src={`${BACKEND_URL}${recipe.photo_url}`} 
                    alt={recipe.title} 
                    style={{ 
                      width: '100%', 
                      maxHeight: '400px', 
                      objectFit: 'cover', 
                      borderRadius: '12px',
                      marginBottom: '1.5rem'
                    }} 
                  />
                  
                  {/* Title */}
                  <h1 style={{ marginTop: 0, marginBottom: '0.5rem', color: '#c14444', fontSize: '2rem' }}>
                    {recipe.title}
                  </h1>
                  
                  {recipe.subtitle && (
                    <h2 style={{ marginTop: 0, marginBottom: '1.5rem', color: '#888', fontSize: '1.2rem', fontWeight: 400 }}>
                      {recipe.subtitle}
                    </h2>
                  )}

                  {/* Preview Ingredients (first 2) */}
                  {recipe.ingredients && recipe.ingredients.length > 0 && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <h3 style={{ color: '#b24646', marginBottom: '0.75rem', fontSize: '1.3rem' }}>Sample Ingredients:</h3>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {recipe.ingredients.slice(0, 2).map((ingredient: any, idx: number) => (
                          <li key={idx} style={{ 
                            padding: '0.5rem 0', 
                            color: '#333',
                            fontSize: '1.05rem',
                            borderBottom: idx === 0 ? '1px solid #f0f0f0' : 'none'
                          }}>
                            {ingredient.quantity && <span style={{ fontWeight: 600 }}>{ingredient.quantity}</span>} {ingredient.name}
                          </li>
                        ))}
                      </ul>
                      {recipe.ingredients.length > 2 && (
                        <p style={{ marginTop: '0.5rem', color: '#888', fontStyle: 'italic' }}>
                          + {recipe.ingredients.length - 2} more ingredients...
                        </p>
                      )}
                    </div>
                  )}

                  {/* Blurred Content Overlay */}
                  <div 
                    style={{
                      position: 'relative',
                      background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.95) 50%, rgba(255,255,255,1) 100%)',
                      padding: '3rem 0 1rem',
                      marginTop: '-2rem',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      setHasInteracted(true);
                    }}
                  >
                    <div style={{ 
                      filter: 'blur(8px)', 
                      userSelect: 'none',
                      pointerEvents: 'none'
                    }}>
                      <h3 style={{ color: '#b24646', marginBottom: '0.75rem' }}>Full Recipe Details</h3>
                      <p style={{ lineHeight: 1.8, color: '#666' }}>
                        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Detailed cooking instructions, 
                        nutritional information, and complete ingredient list available after unlocking...
                      </p>
                    </div>
                  </div>
                </div>

                {/* Unlock CTA */}
                <div style={{ background: '#fff', borderRadius: 16, padding: '1.5rem', border: '2px solid #c14444', textAlign: 'center' }}>
                  {!hasInteracted ? (
                    <>
                      <h2 style={{ marginTop: 0, marginBottom: '0.75rem', color: '#c14444', fontSize: '1.5rem' }}>
                        🔒 Unlock Full Recipe
                      </h2>
                      <p style={{ marginTop: 0, marginBottom: '1rem', color: '#555', lineHeight: 1.6 }}>
                        Get access to complete ingredients, step-by-step instructions, nutritional information, and personalized portions for your dog.
                      </p>
                    </>
                  ) : (
                    <>
                      <h2 style={{ marginTop: 0, marginBottom: '0.75rem', color: '#c14444', fontSize: '1.5rem' }}>
                        This recipe is part of Premium
                      </h2>
                      <p style={{ marginTop: 0, marginBottom: '1rem', color: '#555', lineHeight: 1.6 }}>
                        Unlock it and our growing recipe library tailored for your dog.
                      </p>
                    </>
                  )}
                  {/* {unlocksRemaining != null && (
                    <p style={{ marginTop: 0, marginBottom: '1rem', color: '#c14444', fontWeight: 600, fontSize: '1.05rem' }}>
                      {unlocksRemaining} {unlocksRemaining === 1 ? 'unlock' : 'unlocks'} remaining
                    </p>
                  )} */}

                  <button
                    className={`${buttonStyles.antBtn} ${buttonStyles.antBtnPrimary}`}
                    onClick={() => navigate('/pricing')}
                    style={{ width: '100%', maxWidth: 420, fontSize: '1.1rem', padding: '0.9rem 2rem' }}
                  >
                    🔓 Unlock This Recipe
                  </button>

                  <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                    {/* <Link to={`/signup?redirectTo=${encodeURIComponent(`/recipes/${id}${window.location.search || ''}`)}`} className={buttonStyles.textLinkBtn}>
                      Create free account
                    </Link>
                    <span style={{ color: '#999' }}>•</span> */}
                    <Link to={`/login?redirectTo=${encodeURIComponent(`/recipes/${slug}${window.location.search || ''}`)}`} className={buttonStyles.textLinkBtn}>
                      Log in
                    </Link>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="recipe-bg-container">
      {/* Flash Message */}
      {flashMessage && (
        <FlashMessage
          message={flashMessage.message}
          type={flashMessage.type}
          onClose={() => setFlashMessage(null)}
        />
      )}
      
      {/* Leave Warning Modal */}
      {showLeaveModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          animation: 'fadeIn 0.2s ease-out'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            animation: 'fadeIn 0.2s ease-out'
          }}>
            <div style={{ marginBottom: '1rem' }}>
              <img src={DogIcon} alt="" style={{ width: '64px', height: '64px' }} />
            </div>
            <h2 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1.5rem', fontWeight: 700 }}>
              Keep your dog’s portions for next time
            </h2>
            <p style={{ margin: '0 0 0.5rem 0', color: '#666', fontSize: '1.05rem' }}>
             Save your dog’s details and portion calculations for next time.
            </p>
            <p style={{ margin: '0 0 2rem 0', color: '#666', fontSize: '1.05rem' }}>
              Otherwise, you’ll need to re-enter them when you come back.
            </p>

            {freePortionCalculationsRemaining > 0 && (
              <p style={{ marginBottom: '1.5rem', color: '#b24646', fontSize: '0.95rem', fontWeight: 600, textAlign: 'center' }}>
                {freePortionCalculationsRemaining} free portion calculation{freePortionCalculationsRemaining !== 1 ? 's' : ''} remaining
              </p>
            )}

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button 
                className={`${buttonStyles.antBtn} ${buttonStyles.antBtnPrimary}`}
                onClick={() => {
                  setShowLeaveModal(false);
                  setPendingNavigation(null);
                  navigate('/pricing');
                }}
                style={{ padding: '0.75rem 2rem', fontSize: '1.05rem' }}
              >
                Get Unlimited Calculations
              </button>
              <button 
                className={`${buttonStyles.antBtn} ${buttonStyles.antBtnSecondary}`}
                onClick={() => {
                  setShowLeaveModal(false);
                  setPendingNavigation(null);
                }}
                style={{ padding: '0.75rem 2rem', fontSize: '1.05rem' }}
              >
                Stay on Page
              </button>
            </div>

            <button
              onClick={() => {
                if (pendingNavigation) {
                  navigate(pendingNavigation);
                }
                setShowLeaveModal(false);
                setPendingNavigation(null);
              }}
              style={{
                marginTop: '1.5rem',
                background: 'transparent',
                border: 'none',
                color: '#999',
                fontSize: '0.9rem',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              Leave anyway
            </button>
          </div>
        </div>
      )}
      
      {/* Mobile sticky back bar */}
      {/* ...existing code... */}
      <div className="recipe-paw-bg">
        <img src={pawImg} className="recipe-paw-float paw1" alt="" />
        <img src={pawImg} className="recipe-paw-float paw2" alt="" />
        <img src={pawImg} className="recipe-paw-float paw3" alt="" />
        <img src={pawImg} className="recipe-paw-float paw4" alt="" />
        <img src={pawImg} className="recipe-paw-float paw5" alt="" />
        <img src={pawImg} className="recipe-paw-float paw6" alt="" />
        <img src={pawImg} className="recipe-paw-float paw7" alt="" />
      </div>
      {recipe && (
        <div className="recipe-content-wrapper">
          <div className="details-container">
            {/* Left Side: Image, Title*/}
            <div className="left-top">
              {/* Back Button (web/tablet and mobile) */}
              {isMobile ? (
                <button
                  className={`${buttonStyles.antBtn} ${buttonStyles.antBtnSecondary} mobile-back-btn`}
                  style={{ marginBottom: 14, fontSize: '1.08rem', padding: '0.7em 1.6em', borderRadius: '14px', fontWeight: 600, alignSelf: 'flex-start' }}
                  onClick={() => {
                    const params = new URLSearchParams(window.location.search);
                    const from = params.get('from');
                    const scrollToRecipes = params.get('scrollToRecipes');
                    let destination = '/recipes';
                    if (showPortionsInUrl) {
                      destination = '/recipe-builder/results';
                    } else if (from === 'home') {
                      destination = scrollToRecipes === 'true' ? '/?scrollToRecipes=true' : '/';
                    }
                    
                    if (showNavigateWarning && user && !isPremium(user) && !isAdmin(user)) {
                      setPendingNavigation(destination);
                      setShowLeaveModal(true);
                      return;
                    }
                    navigate(destination);
                  }}
                  aria-label={showPortionsInUrl ? "Back to results" : (new URLSearchParams(window.location.search).get('from') === 'home' ? "Back to home" : "Back to recipes")}
                >
                  <span className="mobile-back-arrow" aria-hidden>←</span>
                  <span className="mobile-back-label">{showPortionsInUrl ? "Back to Results" : (new URLSearchParams(window.location.search).get('from') === 'home' ? "Back to Home" : "Back to Recipes")}</span>
                </button>
              ) : (
                <button
                  className={`${buttonStyles.antBtn} ${buttonStyles.antBtnSecondary}`}
                  style={{ marginBottom: 18, fontSize: '1.08rem', padding: '0.7em 1.6em', borderRadius: '14px', fontWeight: 600, alignSelf: 'flex-start' }}
                  onClick={() => {
                    const params = new URLSearchParams(window.location.search);
                    const from = params.get('from');
                    const scrollToRecipes = params.get('scrollToRecipes');
                    let destination = '/recipes';
                    if (showPortionsInUrl) {
                      destination = '/recipe-builder/results';
                    } else if (from === 'home') {
                      destination = scrollToRecipes === 'true' ? '/?scrollToRecipes=true' : '/';
                    }
                    
                    if (showNavigateWarning && user && !isPremium(user) && !isAdmin(user)) {
                      setPendingNavigation(destination);
                      setShowLeaveModal(true);
                      return;
                    }
                    navigate(destination);
                  }}
                  aria-label={showPortionsInUrl ? "Back to results" : (new URLSearchParams(window.location.search).get('from') === 'home' ? "Back to home" : "Back to recipes")}
                >
                  ← {showPortionsInUrl ? "Back to Results" : (new URLSearchParams(window.location.search).get('from') === 'home' ? "Back to Home" : "Back to Recipes")}
                </button>
              )}
              <img src={`${BACKEND_URL}${recipe.photo_url}`} alt={recipe.title} className="recipe-hero-img" />
              <div style={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '16px'}}>
                <h1 className="recipe-title" style={{margin: 0}}>{recipe.title}</h1>
                {/* Add to Favorites button for premium/admin users */}
                {user && (isPremium(user) || isAdmin(user)) && (
                  isFavorited ? (
                    <button
                      className={`${buttonStyles.antBtn} ${buttonStyles.antBtnSecondary}`}
                      onClick={handleToggleFavorite}
                      disabled={favoriteLoading}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0.5rem 1rem', background: '#FFF5F0', transition: 'transform 0.2s ease, background 0.2s ease', marginTop: isMobile ? '12px' : '0' }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    >
                      <img 
                        src={StarFillIcon} 
                        alt="" 
                        style={{ 
                          width: '20px', 
                          height: '20px', 
                          filter: 'invert(34%) sepia(29%) saturate(1454%) hue-rotate(325deg) brightness(92%) contrast(89%)',
                          animation: justSaved ? 'starPop 0.6s ease' : 'none'
                        }} 
                      />
                      Saved
                    </button>
                  ) : (
                    <button
                      className={`${buttonStyles.antBtn} ${buttonStyles.antBtnSecondary}`}
                      onClick={handleToggleFavorite}
                      disabled={favoriteLoading}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0.5rem 1rem', background: '#FFF5F0', transition: 'transform 0.2s ease, background 0.2s ease', marginTop: isMobile ? '12px' : '0' }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    >
                      <img src={StarIcon} alt="" style={{ width: '20px', height: '20px', filter: 'invert(34%) sepia(29%) saturate(1454%) hue-rotate(325deg) brightness(92%) contrast(89%)' }} />
                      Save Recipe
                    </button>
                  )
                )}
              </div>
              {/* Optional subtitle example */}
              {recipe.subtitle && (
                <h2 className="recipe-subtitle">{recipe.subtitle}</h2>
              )}
            </div>
            {/* Right Side: Portions Card */}
            <div className="right-side">
              {/* Show portion toggle button only on mobile. If no dog data, button opens modal directly. If dog data, toggles panel. */}
              {isMobile && (
                (dogProfile.age && dogProfile.weight && dogProfile.meals_per_day && dogProfile.body_condition) ? (
                  <button
                    className={`${buttonStyles.antBtn} ${buttonStyles.antBtnSecondary}`}
                    style={{ fontSize: '1rem', padding: '0.4em 1.2em', fontWeight: 700, marginBottom: '12px'}}
                    onClick={() => setPortionExpanded(v => !v)}
                    aria-expanded={portionExpanded}
                    aria-controls="portion-panel"
                  >
                    {portionToggleLabel}
                  </button>
                ) : (
                  <>
                    {user && !isPremium(user) && !isAdmin(user) && (user.calculations_remaining === 0 || user.calculations_remaining === null) ? (
                      <div style={{ textAlign: 'center', padding: '1rem', background: '#fff5f0', borderRadius: '12px', border: '1px solid #ffe5c2', marginBottom: '12px' }}>
                        <p style={{ marginTop: 0, color: '#4a4a4a', fontWeight: 600, fontSize: '1rem' }}>Limit Reached</p>
                        <p style={{ color: '#4a4a4a', fontSize: '0.9rem', marginBottom: '0.8rem' }}><span style={{ color: '#c14444', fontWeight: 600 }}>Upgrade</span> for unlimited portion calculations</p>
                        <button
                          className={`${buttonStyles.antBtn} ${buttonStyles.antBtnPrimary}`}
                          onClick={() => navigate('/pricing')}
                          style={{ fontSize: '0.9rem', padding: '0.6rem 1.2rem' }}
                        >
                          Upgrade to Premium
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          className={`${buttonStyles.antBtn} ${buttonStyles.antBtnSecondary}`}
                          onClick={() => setOpenProfileModal(true)}
                          aria-controls="portion-panel"
                          disabled={!!(user && !isPremium(user) && !isAdmin(user) && user.calculations_remaining !== null && user.calculations_remaining !== undefined && user.calculations_remaining === 0)}
                        >
                          Calculate portions for your dog
                        </button>
                        {user && !isPremium(user) && !isAdmin(user) && user.calculations_remaining !== null && user.calculations_remaining !== undefined && (
                          <p style={{ fontSize: '0.85rem', color: '#888', marginTop: '0.5rem', marginBottom: '12px' }}>
                            {user.calculations_remaining} calculation{user.calculations_remaining !== 1 ? 's' : ''} remaining
                          </p>
                        )}
                      </>
                    )}
                  </>
                )
              )}

              {/* Collapsible panel (hidden on mobile when collapsed; always shown on desktop) */}
              <div id="portion-panel" className={`portion-panel ${portionExpanded ? "is-open" : "is-closed"}`} hidden={isMobile && !portionExpanded} role="region" aria-labelledby="portion-panel-label">
                {!portionData ? (
                  !isMobile ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
                      {user && !isPremium(user) && !isAdmin(user) && user.calculations_remaining === 0 ? (
                        <div style={{ textAlign: 'center', padding: '2rem', background: '#fff5f0', borderRadius: '12px', border: '1px solid #ffe5c2' }}>
                          <p style={{ marginTop: 0, color: '#4a4a4a', fontWeight: 600, fontSize: '1.1rem' }}>Portion Calculator Limit Reached</p>
                          <p style={{ color: '#4a4a4a', marginBottom: '1rem' }}>You've used all your free portion calculations. <span style={{ color: '#c14444', fontWeight: 600 }}>Upgrade</span> to premium for unlimited access!</p>
                          <button
                            className={`${buttonStyles.antBtn} ${buttonStyles.antBtnPrimary}`}
                            onClick={() => navigate('/pricing')}
                            style={{ fontSize: '1rem', padding: '0.8rem 1.5rem', borderRadius: '12px', fontWeight: 600 }}
                          >
                            Upgrade to Premium
                          </button>
                        </div>
                      ) : (
                        <>
                          <button
                            className={`${buttonStyles.antBtn} ${buttonStyles.antBtnPrimary}`}
                            style={{ marginBottom: 8, fontSize: '1.08rem', padding: '1rem 2.1rem', borderRadius: '18px', fontWeight: 600 }}
                            onClick={() => setOpenProfileModal(true)}
                            disabled={!!(user && !isPremium(user) && !isAdmin(user) && user.calculations_remaining === 0)}
                          >
                            Calculate Portions for Your Dog
                          </button>
                          {user && !isPremium(user) && !isAdmin(user) && user.calculations_remaining !== null && user.calculations_remaining !== undefined && (
                            <p style={{ fontSize: '0.9rem', color: '#888', margin: 0 }}>
                              {user.calculations_remaining === 3 ? (
                                <>Calculate portions tailored to your dog</>
                              ) : (
                                <>{user.calculations_remaining} of 3 free calculation{user.calculations_remaining !== 1 ? 's' : ''} remaining</>
                              )}
                            </p>
                          )}
                        </>
                      )}
                      <DogProfileModal
                        open={openProfileModal}
                        onClose={() => setOpenProfileModal(false)}
                        onSubmit={handleProfileModalSubmit}
                        initialProfile={dogProfile}
                        ageOptions={[">1","2","3","4","5","6","7","8","9","10","11","12","13","14","15","<15"]}
                      />
                    </div>
                  ) : (
                    <DogProfileModal
                      open={openProfileModal}
                      onClose={() => setOpenProfileModal(false)}
                      onSubmit={handleProfileModalSubmit}
                      initialProfile={dogProfile}
                      ageOptions={[">1","2","3","4","5","6","7","8","9","10","11","12","13","14","15","<15"]}
                    />
                  )
                ) : (
                  <div className="portion-card__inner">
                    {/* Header */}
                    <div className="portion-card__header" style={{ marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="portion-chip">Personalized</span>
                        <div style={{ position: 'relative', display: 'inline-flex' }}>
                          <button
                            type="button"
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              padding: 0,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) => {
                              setShowCalcTooltip(true);
                              e.currentTarget.style.background = 'rgba(193, 68, 68, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                              setShowCalcTooltip(false);
                              e.currentTarget.style.background = 'transparent';
                            }}
                            onClick={() => setShowCalcTooltip(v => !v)}
                            aria-label="How portions are calculated"
                          >
                            <InfoIcon style={{ width: 18, height: 18, fill: '#d97706' }} aria-hidden="true" />
                          </button>
                          {showCalcTooltip && (
                            <div className="portion-tooltip">                              <div style={{ marginBottom: 6, fontWeight: 600 }}>How portions are calculated:</div>
                              <div>Based on your dog's weight, age, body condition, and feeding schedule using veterinary nutrition guidelines.</div>
                              <br />
                              <div>Dogs with different weights, ages, and body conditions have varying daily nutritional needs.</div>
                              <br />
                              <Link to="/feeding-portions" style={{ color: '#d97706', fontWeight: 600, textDecoration: 'underline' }}>
                                Learn more about feeding portions →
                              </Link>
                              <div
                                style={{
                                  position: 'absolute',
                                  top: -6,
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  width: 0,
                                  height: 0,
                                  borderLeft: '6px solid transparent',
                                  borderRight: '6px solid transparent',
                                  borderBottom: '6px solid #2c3e50',
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                      <h3 className="portion-title" style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.5px', color: '#c14444', margin: 0 }}>
                        Portions for {dogProfile.name ? capitalizeFirst(dogProfile.name) : 'your dog'}
                      </h3>
                    </div>
                    {/* Selector */}
                    <div className="portion-label" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <span className="portion-label__text" style={{ fontWeight: 'bold' }}>Batch size</span>
                      <div className="segmented-btn-group" style={{ width: 'fit-content', display: 'inline-flex', borderRadius: 8, overflow: 'hidden', border: '1.5px solid #c14444', boxShadow: '0 1px 4px rgba(44,62,80,0.07)' }}>
                        <button
                          type="button"
                          className={portionMultiplier === 0.25 ? 'segmented-btn segmented-btn--active' : 'segmented-btn segmented-btn--inactive'}
                          style={{
                            fontWeight: portionMultiplier === 0.25 ? 700 : 500,
                            background: portionMultiplier === 0.25 ? '#C44735' : '#fff',
                            color: portionMultiplier === 0.25 ? '#fffbe6' : '#c14444',
                            border: 'none',
                            borderRight: '1.5px solid #c14444',
                            padding: '8px 22px',
                            cursor: 'pointer',
                            outline: 'none',
                            transition: 'background 0.18s, color 0.18s',
                            borderRadius: '8px 0 0 8px',
                          }}
                          onClick={() => {
                            setIsBatchResize(true);
                            setPortionMultiplier(0.25);
                            setBatchSize('Sample');
                          }}
                          onMouseOver={e => {
                            if (portionMultiplier === 0.25) {
                              e.currentTarget.style.background = '#9E362A';
                            } else {
                              e.currentTarget.style.background = '#f1d9d9';
                            }
                          }}
                          onMouseOut={e => {
                            e.currentTarget.style.background = portionMultiplier === 0.25 ? '#C44735' : '#fff';
                          }}
                        >
                          Sample
                        </button>
                        <button
                          type="button"
                          className={portionMultiplier === 0.5 ? 'segmented-btn segmented-btn--active' : 'segmented-btn segmented-btn--inactive'}
                          style={{
                            fontWeight: portionMultiplier === 0.5 ? 700 : 500,
                            background: portionMultiplier === 0.5 ? '#C44735' : '#fff',
                            color: portionMultiplier === 0.5 ? '#fffbe6' : '#c14444',
                            border: 'none',
                            borderRight: '1.5px solid #c14444',
                            padding: '8px 22px',
                            cursor: 'pointer',
                            outline: 'none',
                            transition: 'background 0.18s, color 0.18s',
                            borderRadius: '0'
                          }}
                          onClick={() => {
                            setIsBatchResize(true);
                            setPortionMultiplier(0.5);
                            setBatchSize('Half');
                          }}
                          onMouseOver={e => {
                            if (portionMultiplier === 0.5) {
                              e.currentTarget.style.background = '#9E362A';
                            } else {
                              e.currentTarget.style.background = '#f1d9d9';
                            }
                          }}
                          onMouseOut={e => {
                            e.currentTarget.style.background = portionMultiplier === 0.5 ? '#C44735' : '#fff';
                          }}
                        >
                          Half
                        </button>
                        <button
                          type="button"
                          className={portionMultiplier === 1 ? 'segmented-btn segmented-btn--active' : 'segmented-btn segmented-btn--inactive'}
                          style={{
                            fontWeight: portionMultiplier === 1 ? 700 : 500,
                            background: portionMultiplier === 1 ? '#C44735' : '#fff',
                            color: portionMultiplier === 1 ? '#fffbe6' : '#c14444',
                            border: 'none',
                            borderRight: '1.5px solid #c14444',
                            padding: '8px 22px',
                            cursor: 'pointer',
                            outline: 'none',
                            transition: 'background 0.18s, color 0.18s',
                            borderRadius: '0',
                          }}
                          onClick={() => {
                            setIsBatchResize(true);
                            setPortionMultiplier(1);
                            setBatchSize('Normal');
                          }}
                          onMouseOver={e => {
                            if (portionMultiplier === 1) {
                              e.currentTarget.style.background = '#9E362A';
                            } else {
                              e.currentTarget.style.background = '#f1d9d9';
                            }
                          }}
                          onMouseOut={e => {
                            e.currentTarget.style.background = portionMultiplier === 1 ? '#C44735' : '#fff';
                          }}
                        >
                          Normal
                        </button>
                        <button
                          type="button"
                          className={portionMultiplier === 2 ? 'segmented-btn segmented-btn--active' : 'segmented-btn segmented-btn--inactive'}
                          style={{
                            fontWeight: portionMultiplier === 2 ? 700 : 500,
                            background: portionMultiplier === 2 ? '#C44735' : '#fff',
                            color: portionMultiplier === 2 ? '#fffbe6' : '#c14444',
                            border: 'none',
                            padding: '8px 22px',
                            cursor: 'pointer',
                            outline: 'none',
                            transition: 'background 0.18s, color 0.18s',
                            borderRadius: '0 8px 8px 0',
                          }}
                          onClick={() => {
                            setIsBatchResize(true);
                            setPortionMultiplier(2);
                            setBatchSize('Double');
                          }}
                          onMouseOver={e => {
                            if (portionMultiplier === 2) {
                              e.currentTarget.style.background = '#9E362A';
                            } else {
                              e.currentTarget.style.background = '#f1d9d9';
                            }
                          }}
                          onMouseOut={e => {
                            e.currentTarget.style.background = portionMultiplier === 2 ? '#C44735' : '#fff';
                          }}
                        >
                          Double
                        </button>
                      </div>
                    </div>
                    <div className="portion-selector-note" style={{ fontSize: 12, color: 'rgba(44,62,80,0.65)', marginTop: 2, maxWidth: '60ch', lineHeight: 1.5 }}>
                      Adjusting <b>Batch size</b> updates the <b>ingredients list</b> and <b>portion sizes</b> automatically.
                    </div>

                    <div className="paw-divider" aria-hidden />
                    {/* Updated checkmark and aria-live message */}
                    {showUpdated && (
                      <div style={{ display: 'flex', alignItems: 'center'}}>
                        <span aria-hidden="true" style={{ color: 'green', fontSize: 18, display: 'inline-flex', alignItems: 'center', transition: 'opacity 0.2s', opacity: showUpdated ? 1 : 0 }}>
                          ✓
                        </span>
                        <span style={{ fontSize: 13, color: 'green', fontWeight: 500 }}>Updated</span>
                        <span style={{ position: 'absolute', left: '-9999px' }} aria-live="polite">{ariaMessage}</span>
                      </div>
                    )}
                    {/* Portion Metrics */}
                    <div className="portion-metrics" style={{ display: 'flex', flexDirection: 'column', gap: 10, opacity: portionLoading ? 0.5 : 1, transition: 'opacity 0.2s ease' }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#888', flex: '0 0 140px' }}>Daily portion</span>
                        <span style={{ fontSize: 18, fontWeight: 700, color: '#222', marginLeft: 32, fontVariantNumeric: 'tabular-nums' }}>
                          {dailyGrams} g
                        </span>
                        {displayDailyCups && (
                          <span style={{ fontSize: 14, color: 'rgba(44,62,80,0.60)', marginLeft: 8 }}>
                            (≈{displayDailyCups} cups)
                          </span>
                        )}
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#888', flex: '0 0 140px' }}>Serving size (per meal)</span>
                        <span style={{ fontSize: 18, fontWeight: 700, color: '#222', marginLeft: 32, fontVariantNumeric: 'tabular-nums' }}>
                          {gramsPerMeal} g
                        </span>
                        {displayMealCups && (
                          <span style={{ fontSize: 14, color: 'rgba(44,62,80,0.60)', marginLeft: 8 }}>
                            (≈{displayMealCups} cups)
                          </span>
                        )}
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#888', flex: '0 0 140px' }}>Meals from this batch</span>
                        <span style={{ fontSize: 18, fontWeight: 700, color: '#222', marginLeft: 32, fontVariantNumeric: 'tabular-nums' }}>
                          {recipeMeals} meals
                        </span>
                        <span style={{ fontSize: 12, color: 'rgba(44,62,80,0.65)', marginLeft: 8 }}>
                          at {dogProfile.meals_per_day} meal{dogProfile.meals_per_day === 1 ? '' : 's'}/day
                        </span>
                      </div>
                      {/* Days/Weeks/Months breakdown under meals count */}
                      <div style={{ marginLeft: 172, marginTop: 2, fontSize: 14, color: '#b24646', fontWeight: 600, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span>{recipeDays} days</span>
                        {/* Weeks breakdown */}
                        {recipeDays > 7 && recipeDays <= 30 && (() => {
                          const weeks = Math.floor(recipeDays / 7);
                          const days = Math.round(recipeDays % 7);
                          return (
                            <span>
                              {weeks} week{weeks !== 1 ? 's' : ''}
                              {days > 0 ? ` and ${days} day${days !== 1 ? 's' : ''}` : ''}
                            </span>
                          );
                        })()}
                        {/* Months breakdown */}
                        {recipeDays > 30 && (() => {
                          const months = Math.floor(recipeDays / 30);
                          const remDays = recipeDays % 30;
                          const weeks = Math.floor(remDays / 7);
                          const days = Math.round(remDays % 7);
                          return (
                            <span>
                              {months} month{months !== 1 ? 's' : ''}
                              {weeks > 0 ? `, ${weeks} week${weeks !== 1 ? 's' : ''}` : ''}
                              {days > 0 ? ` and ${days} day${days !== 1 ? 's' : ''}` : ''}
                            </span>
                          );
                        })()}
                      </div>
                      <p style={{ fontSize: 12, color: 'rgba(44,62,80,0.65)', marginTop: 8, fontStyle: 'italic', lineHeight: 1.4 }}>*Batch duration is an estimate. Actual meals may vary slightly depending on ingredient weights and preparation.*</p>
                    </div>
                    
                    {/* Compact Dog Profile Card */}
                    <div className="portion-profile-card" style={{ margin: '16px 0 8px 0', background: '#fffbe6', borderRadius: 10, boxShadow: '0 1px 6px rgba(44,62,80,0.07)', padding: '0.7em 1em', display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start', minHeight: 56 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <span style={{ fontWeight: 600, fontSize: 15, color: '#c14444', letterSpacing: '-0.5px' }}>
                          {dogProfile.name ? `${capitalizeFirst(dogProfile.name)}'s Profile` : 'Dog Profile'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
                        <span style={{ background: '#ffe5c2', color: '#c14444', fontWeight: 600, fontSize: 13, borderRadius: 12, padding: '2px 12px', display: 'inline-block' }}>
                          Age: {dogProfile.age ? dogProfile.age.replace(/[^\d]+/, '') + 'y' : 'N/A'}
                        </span>
                        <span style={{ background: '#ffe5c2', color: '#c14444', fontWeight: 600, fontSize: 13, borderRadius: 12, padding: '2px 12px', display: 'inline-block' }}>
                          Weight: {dogProfile.weight ? `${Number(dogProfile.weight).toFixed(1)} lb / ${(Number(dogProfile.weight) / 2.20462).toFixed(2)} kg` : 'N/A'}
                        </span>
                        <span style={{ background: '#ffe5c2', color: '#c14444', fontWeight: 600, fontSize: 13, borderRadius: 12, padding: '2px 12px', display: 'inline-block' }}>
                          Meals per day: {dogProfile.meals_per_day || 'N/A'}
                        </span>
                        <span style={{ background: '#ffe5c2', color: '#c14444', fontWeight: 600, fontSize: 13, borderRadius: 12, padding: '2px 12px', display: 'inline-block' }}>
                          Body Condition: {dogProfile.body_condition || 'N/A'}
                        </span>
                        {dogProfile.allergies && (
                          <span style={{ background: '#ffe5c2', color: '#c14444', fontWeight: 600, fontSize: 13, borderRadius: 12, padding: '2px 12px', display: 'inline-block' }}>
                            Allergies: {dogProfile.allergies}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* CTA */}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className={`${buttonStyles.antBtn} ${buttonStyles.antBtnSecondary}`}
                        onClick={() => setOpenProfileModal(true)}
                      >
                        <span role="img" aria-label="edit" style={{ marginRight: 8 }}>✏️</span> 
                        Edit Dog Details
                      </button>
                      {user && (isPremium(user) || isAdmin(user)) && (
                        <button
                          type="button"
                          className={`${buttonStyles.antBtn} ${buttonStyles.antBtnSecondary}`}
                          onClick={() => {
                            dogProfileSnapshot.current = dogProfile;
                            setDogProfile({ age: '', weight: '', meals_per_day: 1, body_condition: '', allergies: '' });
                            setOpenProfileModal(true);
                          }}
                          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <circle cx="9" cy="9" r="9" fill="#c14444"/>
                            <path d="M9 5v8M5 9h8" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                          <span>Add another dog</span>
                        </button>
                      )}
                    </div>
                    <DogProfileModal
                      open={openProfileModal}
                      onClose={() => {
                        // If modal is closed without saving, restore previous dog profile if snapshot exists
                        if (dogProfileSnapshot.current) {
                          setDogProfile(dogProfileSnapshot.current);
                          dogProfileSnapshot.current = null;
                        }
                        setOpenProfileModal(false);
                      }}
                      onSubmit={handleProfileModalSubmit}
                      initialProfile={dogProfile}
                      ageOptions={[">1","2","3","4","5","6","7","8","9","10","11","12","13","14","15","<15"]}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Left Side: Ingredients, Instructions */}
            <div className="left-main">
              {/* balance section */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
                {/* Batch Size Selector - Show for free users with no calculations remaining only */}
                {(user && !isPremium(user) && !isAdmin(user) && (user.calculations_remaining === null || user.calculations_remaining === undefined || user.calculations_remaining <= 0)) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#888' }}>Adjust batch size:</span>
                    <div style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: '1.5px solid #c14444' }}>
                    <button
                      type="button"
                      style={{
                        fontWeight: portionMultiplier === 0.25 ? 700 : 500,
                        background: portionMultiplier === 0.25 ? '#C44735' : '#fff',
                        color: portionMultiplier === 0.25 ? '#fffbe6' : '#c14444',
                        border: 'none',
                        borderRight: '1.5px solid #c14444',
                        padding: '4px 12px',
                        cursor: 'pointer',
                        fontSize: '0.85em',
                        transition: 'background 0.18s, color 0.18s',
                      }}
                      onClick={() => {
                        setPortionMultiplier(0.25);
                        setBatchSize('Sample');
                      }}
                      onMouseOver={e => {
                        if (portionMultiplier !== 0.25) e.currentTarget.style.background = '#f1d9d9';
                      }}
                      onMouseOut={e => {
                        if (portionMultiplier !== 0.25) e.currentTarget.style.background = '#fff';
                      }}
                    >
                      ¼
                    </button>
                    <button
                      type="button"
                      style={{
                        fontWeight: portionMultiplier === 0.5 ? 700 : 500,
                        background: portionMultiplier === 0.5 ? '#C44735' : '#fff',
                        color: portionMultiplier === 0.5 ? '#fffbe6' : '#c14444',
                        border: 'none',
                        borderRight: '1.5px solid #c14444',
                        padding: '4px 12px',
                        cursor: 'pointer',
                        fontSize: '0.85em',
                        transition: 'background 0.18s, color 0.18s',
                      }}
                      onClick={() => {
                        setPortionMultiplier(0.5);
                        setBatchSize('Half');
                      }}
                      onMouseOver={e => {
                        if (portionMultiplier !== 0.5) e.currentTarget.style.background = '#f1d9d9';
                      }}
                      onMouseOut={e => {
                        if (portionMultiplier !== 0.5) e.currentTarget.style.background = '#fff';
                      }}
                    >
                      ½
                    </button>
                    <button
                      type="button"
                      style={{
                        fontWeight: portionMultiplier === 1 ? 700 : 500,
                        background: portionMultiplier === 1 ? '#C44735' : '#fff',
                        color: portionMultiplier === 1 ? '#fffbe6' : '#c14444',
                        border: 'none',
                        borderRight: '1.5px solid #c14444',
                        padding: '4px 12px',
                        cursor: 'pointer',
                        fontSize: '0.85em',
                        transition: 'background 0.18s, color 0.18s',
                      }}
                      onClick={() => {
                        setPortionMultiplier(1);
                        setBatchSize('Normal');
                      }}
                      onMouseOver={e => {
                        if (portionMultiplier !== 1) e.currentTarget.style.background = '#f1d9d9';
                      }}
                      onMouseOut={e => {
                        if (portionMultiplier !== 1) e.currentTarget.style.background = '#fff';
                      }}
                    >
                      1×
                    </button>
                    <button
                      type="button"
                      style={{
                        fontWeight: portionMultiplier === 2 ? 700 : 500,
                        background: portionMultiplier === 2 ? '#C44735' : '#fff',
                        color: portionMultiplier === 2 ? '#fffbe6' : '#c14444',
                        border: 'none',
                        padding: '4px 12px',
                        cursor: 'pointer',
                        fontSize: '0.85em',
                        transition: 'background 0.18s, color 0.18s',
                      }}
                      onClick={() => {
                        setPortionMultiplier(2);
                        setBatchSize('Double');
                      }}
                      onMouseOver={e => {
                        if (portionMultiplier !== 2) e.currentTarget.style.background = '#f1d9d9';
                      }}
                      onMouseOut={e => {
                        if (portionMultiplier !== 2) e.currentTarget.style.background = '#fff';
                      }}
                    >
                      2×
                    </button>
                  </div>
                  </div>
                )}

                {/* Balancing Methods Section */}
                <div style={{marginBottom: 24, background: '#fffbe6', borderRadius: 12}}>
                  <h3 style={{marginBottom: 24, fontSize: '1.1rem', fontWeight: 600}}>Choose how you'd like to balance this recipe</h3>
                  <div style={{marginBottom: 10, fontSize: '1.01em'}}>
                    <span>If you have questions about supplements, see our{' '}
                      <a href="/supplements" style={{textDecoration: 'underline', fontWeight: 600}}>Supplement & Balancing Guide</a>.
                    </span>
                  </div>
                  <div style={{display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 8}}>
                    {/* Option supplement Card */}
                    <div
                      key="optionSupplement"
                      style={{
                        flex: selectedPath === 'supplement' ? 1 : '0 1 auto',
                        minWidth: selectedPath === 'supplement' ? 260 : 'auto',
                        background: '#f9f7f3',
                        border: selectedPath === 'supplement' ? '2px solid #b24646' : '1.5px solid #d4c4b8',
                        borderRadius: 12,
                        padding: selectedPath === 'supplement' ? '1.1em 1.3em' : '1.2em 1.4em',
                        boxShadow: selectedPath === 'supplement' ? '0 2px 6px rgba(44,62,80,0.08)' : '0 1px 3px rgba(44,62,80,0.06)',
                        marginBottom: 12,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onClick={() => setSelectedPath('supplement')}
                      tabIndex={0}
                      aria-label="Select Option supplement"
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setSelectedPath('supplement'); }}
                    >
                      <span style={{display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: selectedPath === 'supplement' ? 2 : 0}}>
                        <span style={{display: 'inline-block', width: 16, height: 16, minWidth: 16, minHeight: 16, maxWidth: 16, maxHeight: 16, borderRadius: '50%', border: '2px solid #b24646', background: '#fff', boxSizing: 'border-box', position: 'relative', marginRight: 6}} aria-hidden="true">
                          {selectedPath === 'supplement' && <span style={{position: 'absolute', left: 2, top: 2, width: 8, height: 8, borderRadius: '50%', background: '#b24646'}} />}
                        </span>
                        <strong style={{color: '#b24646'}}>Supplement Option: Use a Complete Vitamin–Mineral Supplement</strong>
                      </span>
                      
                      {selectedPath === 'supplement' ? (
                        <>
                          <p style={{fontSize: '0.9em', color: '#888', marginTop: 4, marginBottom: 8}}>Precisely balanced with a complete supplement</p>
                          <p>How to Calculate:</p>
                          {recipeDays > 0 && (
                            <p style={{margin: '6px 0 10px 0', color: '#b24646', fontWeight: 500}}>
                              Based on {dogProfile.name ? `${capitalizeFirst(dogProfile.name)}'s` : "your dog's"} profile: <span style={{fontWeight: 700}}>{recipeDays}</span> feeding days
                            </p>
                          )}
                          {recipeDays > 0 ? (
                            <p style={{paddingLeft: 16}}><strong>Daily supplement amount × {recipeDays} = Total amount to add</strong></p>
                          ) : (
                            <p style={{paddingLeft: 16}}><strong>Daily supplement amount × Number of days shown in "Meals from this batch" = Total amount to add</strong></p>
                          )}
                          <ol style={{marginTop: 6, marginBottom: 6}}>
                            <li>Find the daily amount on your supplement label.</li>
                            <li>Multiply it by the number of feeding days shown on the right. (If you have entered the dog's portions)</li>
                            <li>Mix that total evenly into the batch.</li>
                          </ol>

                          {/* <p style={{fontSize: '0.95em', color: '#666', textAlign: 'left', marginTop: 16, marginBottom: 0}}>
                            For the most precise nutritional balance, a complete vitamin-mineral supplement is recommended. The whole food path is a great alternative if you prefer to avoid supplements.
                          </p> */}
                        </>
                      ) : (
                        <p style={{fontSize: '0.95em', color: '#666', marginTop: 8, marginBottom: 0}}>
                          Use a complete vitamin-mineral supplement for precise balance
                        </p>
                      )}
                    </div>
                    {/* Option wholefood Card */}
                    <div
                      key="optionWholefood"
                      style={{
                        flex: selectedPath === 'wholefood' ? 1 : '0 1 auto',
                        minWidth: selectedPath === 'wholefood' ? 260 : 'auto',
                        background: '#f9f7f3',
                        border: selectedPath === 'wholefood' ? '2px solid #b24646' : '1.5px solid #d4c4b8',
                        borderRadius: 12,
                        padding: selectedPath === 'wholefood' ? '1.1em 1.3em' : '1.2em 1.4em',
                        boxShadow: selectedPath === 'wholefood' ? '0 2px 6px rgba(44,62,80,0.08)' : '0 1px 3px rgba(44,62,80,0.06)',
                        marginBottom: 12,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onClick={() => setSelectedPath('wholefood')}
                      tabIndex={0}
                      aria-label="Select Option wholefood"
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setSelectedPath('wholefood'); }}
                    >
                      <span style={{display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: selectedPath === 'wholefood' ? 6 : 0}}>
                        <span style={{display: 'inline-block', width: 16, height: 16, minWidth: 16, minHeight: 16, maxWidth: 16, maxHeight: 16, borderRadius: '50%', border: '2px solid #b24646', background: '#fff', boxSizing: 'border-box', position: 'relative', marginRight: 6}} aria-hidden="true">
                          {selectedPath === 'wholefood' && <span style={{position: 'absolute', left: 2, top: 2, width: 8, height: 8, borderRadius: '50%', background: '#b24646'}} />}
                        </span>
                        <strong style={{color: '#b24646'}}>Whole Food Option: Use Individual Food-Based Additions</strong>
                      </span>
                      
                      {selectedPath === 'wholefood' ? (
                        <>
                          <p style={{fontSize: '0.9em', color: '#888', marginTop: 4, marginBottom: 12}}>Balanced using whole food ingredients</p>
                          
                          <p style={{marginBottom: 4, fontWeight: 600}}>Includes natural sources of:</p>
                          <ul style={{marginTop: 0, marginBottom: 12, paddingLeft: 20, lineHeight: 1.6}}>
                            <li>Calcium</li>
                            <li>Omega-3 fatty acids</li>
                            <li>Essential micronutrients</li>
                          </ul>

                          <p style={{marginBottom: 0}}>Do not combine with a complete vitamin–mineral supplement</p>
                        </>
                      ) : (
                        <>
                          <p style={{fontSize: '0.95em', color: '#666', marginTop: 8, marginBottom: 8}}>
                            Use whole food ingredients to balance the recipe naturally
                          </p>
                          <p style={{fontSize: '0.88em', color: '#888', marginTop: 8, marginBottom: 0, fontStyle: 'italic'}}>
                            For the most precise nutritional balance, a complete vitamin-mineral supplement is recommended.
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <p style={{fontSize: '0.95em', color: '#666', textAlign: 'left', marginTop: 0, marginBottom: 0}}>
                  Both options support your dog’s nutrition. For the most precise balance, a complete vitamin–mineral supplement is recommended.
                </p>
              </div>
              {/* Main Ingredients Section */}
              <div style={{display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', flexWrap: 'wrap', marginTop: 8, gap: isMobile ? '8px' : '0'}}>
                <h2 style={{marginBottom: 24, marginTop: 0}}>
                  Ingredients - {selectedPath === 'wholefood' ? 'Whole Food Balanced' : 'Supplement Balanced'}
                </h2>


                <button
                  type="button"
                  className={`${buttonStyles.antBtn} ${buttonStyles.antBtnSecondary}`}
                  style={{
                    padding: '2px 10px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: '0.875em',
                    boxShadow: '0 1px 2px rgba(44,62,80,0.07)',
                    minHeight: '28px',
                    marginLeft: isMobile ? 0 : 100
                  }}
                  onClick={() => {
                    // Toggle all accordions: open all if any closed, else close all
                    const coreCount = recipe.ingredients.filter(i => i.category !== 'supplement').length;
                    const supplementCount = recipe.ingredients.filter(i => i.category === 'supplement').length;
                    const anyClosed = [
                      ...Array.from(Array(coreCount).keys()),
                      ...Array.from(Array(supplementCount).keys()).map(i => 1000 + i)
                    ].some(idx => !ingredientAccordionOpen[idx]);
                    const newState: { [key: number]: boolean } = {};
                    // Core ingredients
                    recipe.ingredients.filter(i => i.category !== 'supplement').forEach((_, idx) => {
                      newState[idx] = anyClosed;
                    });
                    // Supplement ingredients
                    recipe.ingredients.filter(i => i.category === 'supplement').forEach((_, idx) => {
                      newState[1000 + idx] = anyClosed;
                    });
                    setIngredientAccordionOpen(newState);
                  }}
                >
                  {(() => {
                    const coreCount = recipe.ingredients.filter(i => i.category !== 'supplement').length;
                    const supplementCount = recipe.ingredients.filter(i => i.category === 'supplement').length;
                    const allOpen = [
                      ...Array.from(Array(coreCount).keys()),
                      ...Array.from(Array(supplementCount).keys()).map(i => 1000 + i)
                    ].every(idx => ingredientAccordionOpen[idx]);
                    return allOpen ? 'Hide all conversions' : 'Show all conversions';
                  })()}
                </button>
              </div>
              <p style={{fontSize: '0.88em', color: '#727272ff', marginBottom: 12, marginTop: isMobile ? '8px' : '0'}}>
                Automatically adjusted based on your selection
              </p>
              <ul className="ingredients-list">
                {recipe.ingredients
                  .filter(i => {
                    // Exclude supplements from core ingredients
                    // if (i.category === 'supplement' || i.category === 'supplement_optional') return false;
                                                  
                    // Always show ingredients that are in both paths
                    if (i.supplement_path && i.whole_food_path) {
                      return true;
                    }
                    
                    // Show path-specific ingredients based on selected path
                    if (selectedPath === 'wholefood') {
                      const show = i.whole_food_path && !i.supplement_path;
                      return show;
                    }
                    if (selectedPath === 'supplement') {
                      const show = i.supplement_path && !i.whole_food_path;
                      return show;
                    }
                    
                    return false;
                  })
                  .map((ingredient, index) => {
                  const { grams, lbOz, approx, isGram, nonGram } = parseAndScaleQuantity(
                    ingredient.quantity,
                    portionMultiplier,
                    ingredient.name
                  );

                  const normName = normalizeIngredientKey(ingredient.name);
                  const isEggshellPowder = normName.includes('eggshell powder');
                  const mainQty = isGram ? grams : nonGram;
                  const isOpen = !!ingredientAccordionOpen[index];

                  // If eggshell powder and not grams, convert tsp/Tbsp to grams for conversion panel
                  let eggshellConversions = null;
                  if (isEggshellPowder && !isGram) {
                    // Parse tsp/Tbsp to grams: 1 tsp ≈ 5g, 1 Tbsp ≈ 15g
                    const tspMatch = ingredient.quantity.match(/([\d.]+)\s*tsp/i);
                    const tbspMatch = ingredient.quantity.match(/([\d.]+)\s*tbsp/i);
                    let gramsValue = 0;
                    if (tspMatch) {
                      gramsValue = parseFloat(tspMatch[1]) * 5 * portionMultiplier;
                    } else if (tbspMatch) {
                      gramsValue = parseFloat(tbspMatch[1]) * 15 * portionMultiplier;
                    }
                    if (gramsValue > 0) {
                      eggshellConversions = {
                        lbOz: gramsToLbOz(gramsValue),
                        approx: gramsToHouseholdUnits(gramsValue, 'eggshell powder'),
                      };
                    }
                  }

                  return (
                    <li key={`${ingredient.name}-${index}`} className="ingredient-item">
                      {/* Main line: canonical quantity + ingredient name */}
                      <div className="ingredient-line-main">
                        <span className="ingredient-qty">{mainQty} </span>
                        <span className="ingredient-name">{ingredient.name}</span>
                        {/* <span className="ingredient-name">{ingredient.category}</span> */}
                      </div>

                      {/* Accordion for conversions */}
                      {((isGram && (lbOz || approx)) || eggshellConversions) && (
                        <div className="ingredient-conversions-accordion">
                          {isOpen && (
                            <div
                              id={`ingredient-conversions-${index}`}
                              className="ingredient-conversions-panel"
                              style={{
                                background: '#fffbe6',
                                borderRadius: 8,
                                marginTop: 6,
                                padding: '8px 30px',
                                fontSize: '0.97em',
                                color: '#222',
                              }}
                            >
                              {/* Gram-based conversions */}
                              {isGram && lbOz && (
                                <div style={{ marginBottom: approx ? 4 : 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{ display: 'inline-flex', alignItems: 'center', color: '#8a8a8a', fontWeight: 500 }}>
                                    <ScalesIcon style={{ width: 18, height: 18, verticalAlign: 'middle' }} aria-label="weight" />
                                  </span>
                                  <span>{lbOz}</span>
                                </div>
                              )}
                              {isGram && approx && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{ display: 'inline-flex', alignItems: 'center', color: '#8a8a8a', fontWeight: 500 }}>
                                    <HouseIcon style={{ width: 18, height: 18, verticalAlign: 'middle' }} aria-label="household unit" />
                                  </span>
                                  <span>{approx}</span>
                                </div>
                              )}
                              {/* Eggshel powder tsp/Tbsp conversions */}
                              {eggshellConversions && (
                                <>
                                  <div style={{ marginBottom: eggshellConversions.approx ? 4 : 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', color: '#8a8a8a', fontWeight: 500 }}>
                                      <ScalesIcon style={{ width: 18, height: 18, verticalAlign: 'middle' }} aria-label="weight" />
                                    </span>
                                    <span>{eggshellConversions.lbOz}</span>
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', color: '#8a8a8a', fontWeight: 500 }}>
                                      <HouseIcon style={{ width: 18, height: 18, verticalAlign: 'middle' }} aria-label="household unit" />
                                    </span>
                                    <span>{eggshellConversions.approx}</span>
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
              <h2 style={{ marginTop: 25}}>Instructions</h2>
              <div className={`instructions-box ${showAllInstructions ? "" : "is-collapsed"}`}>
                <ol className="instructions-numbered-list" style={{ paddingLeft: 24, margin: 0 }}>
                  {/* Custom logic to inject conditional supplement step */}
                  {(() => {
                    // Split instructions into steps
                    const steps = recipe.instructions
                      .split(/\n|\r|(?<=\.) /)
                      .map((step) => step.trim())
                      .filter(Boolean);
                    const result: React.ReactNode[] = [];
                    let i = 0;
                    while (i < steps.length) {
                      // Look for step that starts with 'Once cooled, stir'
                      if (/^Once cooled, stir/i.test(steps[i])) {
                        // Find the end of this step (until next period or end)
                        // We'll treat the current step as the one to replace
                        result.push(
                          <li key={i} style={{ marginBottom: 6 }}>
                            {/* Conditional supplement step */}
                            {selectedPath === 'supplement' && (
                              <span className="instructions-step-number">
                                Once cooled, stir in the {(() => {
                                  const supplements = recipe.ingredients.filter(i => 
                                    i.category === 'supplement' && 
                                    i.supplement_path === true && 
                                    i.whole_food_path === false
                                  );
                                  if (supplements.length > 0) {
                                    const supplementStrings = supplements.map((ingredient) => ingredient.name);
                                    return supplementStrings.join(', ');
                                  }
                                  return 'Complete Vitamin–Mineral Supplement';
                                })()} according to the label amount for this batch.
                                <div style={{ fontSize: '0.97em', color: '#b24646', marginTop: 6 }}>
                                  (See Option supplement instructions above for how much to add.)
                                </div>
                              </span>
                            )}
                            {selectedPath === 'wholefood' && (
                              <span className="instructions-step-number">
                                Once cooled, stir in{' '}
                                {(() => {
                                  const supplements = recipe.ingredients.filter(i => 
                                    i.category === 'supplement' && 
                                    i.whole_food_path === true && 
                                    i.supplement_path === false
                                  );
                                  if (supplements.length === 0) {
                                    return <span style={{ color: '#888' }}>the whole food additions listed above.</span>;
                                  }
                                  const supplementStrings = supplements.map((ingredient) => ingredient.name);
                                  return <span>{supplementStrings.join(', ')}.</span>;
                                })()}
                              </span>
                            )}
                            {!selectedPath && (
                              <span className="instructions-step-number" style={{ color: '#888' }}>
                                <em>Select a balancing method above to see supplement instructions.</em>
                              </span>
                            )}
                          </li>
                        );
                        i++;
                        continue;
                      }
                      // Otherwise, render the step as usual
                      result.push(
                        <li key={i} style={{ marginBottom: 6 }}><span className="instructions-step-number">{steps[i]}</span></li>
                      );
                      i++;
                    }
                    return result;
                  })()}
                </ol>
              </div>
              <button
                className={`${buttonStyles.antBtn} ${buttonStyles.antBtnSecondary}`}
                style={{ fontSize: '0.875em', padding: '0.4em 1.2em', fontWeight: 600, minHeight: '28px',}}
                onClick={() => setShowAllInstructions(s => !s)}
              >
                {showAllInstructions ? "Show less" : "Show more"}
              </button>
              {/* Cooking Tips Block */}
              <div className="cooking-tips-block">
                <h3 className="cooking-tips-title">🍳 Cooking Tips</h3>
                <ul className="cooking-tips-list">
                  <li>Use hand held blender for shredding chicken.</li>
                  <li>Use large container to mix all ingredients thoroughly.</li>
                  <li>Puree fruits and vegetables for smoother texture so your dog can not pick them out.</li>
                  <li>Let food cool before serving to your dog.</li>
                  <li>It is better to have more moisture in the food than not enough.</li>
                </ul>
              </div>
              {/* Storage Tips Block */}
              <div className="storage-tips-block">
                <h3 className="storage-tips-title">🧊 Storage Tips</h3>
                <ul className="storage-tips-list">
                  <li>Refrigerate leftovers within 2 hours of cooking.</li>
                  <li>Use airtight containers to keep food fresh and prevent odors.</li>
                  <li>Label containers with the date to track freshness.</li>
                  <li>Most homemade dog food lasts 5-6 days in the fridge.</li>
                  <li>Freeze portions for longer storage (up to 2 months).</li>
                  <li>Thaw frozen food in the fridge overnight before serving.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecipeDetail;